import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { darken, transparentize } from 'polished';
import styled from 'styled-components';

// Removed pagination-related imports
import { LinkedColumn, LinkedColumnType } from '@/features/canvas/types/linkage';

interface TableFooterProps {
  linkedColumns: LinkedColumn[];
  onSubmit: () => void;
  rowsLength: number;
  onSaveTemplate?: (name: string) => void;
}

interface TableFooterActionProps {
  linkedColumns: LinkedColumn[];
  onSubmit: () => void;
  rowsLength: number;
  onSaveTemplate?: (name: string) => void;
}

const LinkingTableFooterActions = (props: TableFooterActionProps) => {
  const { onSubmit, linkedColumns, onSaveTemplate, rowsLength } = props;
  const [isTemplateSaveDialogOpen, setIsTemplateSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Disable button until Title field is mapped (Title is required for initiatives)
  const isSubmitDisabled = !linkedColumns.some(
    col => col.linkedTargetType === LinkedColumnType.title
  );

  // Pagination removed - showing all rows with scroll

  const handleSaveTemplate = () => {
    if (templateName.trim() && onSaveTemplate) {
      onSaveTemplate(templateName.trim());
      setIsTemplateSaveDialogOpen(false);
      setTemplateName('');
    }
  };

  const handleOpenSaveDialog = () => {
    setIsTemplateSaveDialogOpen(true);
  };

  return (
    <FooterActionsWrapper>
      <FooterTableActions>
        <div /> {/* Spacer to keep layout balanced */}
        <PageState>{t('items_count', `${rowsLength} Items`, { count: rowsLength })}</PageState>
      </FooterTableActions>

      <ButtonGroup>
        {onSaveTemplate && linkedColumns.length > 0 && (
          <SaveTemplateButton onClick={handleOpenSaveDialog} disabled={isSubmitDisabled}>
            {t('editor.sidebar.data_tab.save_mapping', 'Save Mapping')}
          </SaveTemplateButton>
        )}
        <SubmitButton onClick={onSubmit} disabled={isSubmitDisabled}>
          {t('editor.sidebar.data_tab.finalize_import')}
        </SubmitButton>
      </ButtonGroup>

      {/* Save Template Dialog */}
      <Dialog
        open={isTemplateSaveDialogOpen}
        onClose={() => setIsTemplateSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('editor.sidebar.data_tab.save_template_title', 'Save Mapping Template')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="template-name"
            label={t('editor.sidebar.data_tab.template_name_label', 'Template Name')}
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            placeholder={t('editor.sidebar.data_tab.template_name_placeholder', 'e.g. Jira Import')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTemplateSaveDialogOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleSaveTemplate} disabled={!templateName.trim()}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </FooterActionsWrapper>
  );
};

const LinkingTableFooter = (props: TableFooterProps) => {
  const { onSubmit, linkedColumns, onSaveTemplate, rowsLength } = props;

  return (
    <StyledFooterWrapper>
      <LinkingTableFooterActions
        onSubmit={onSubmit}
        linkedColumns={linkedColumns}
        onSaveTemplate={onSaveTemplate}
        rowsLength={rowsLength}
      />
    </StyledFooterWrapper>
  );
};

const PageState = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => transparentize(0.5, theme.palette.brand.textSecondary)};
`;

const FooterTableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
`;

const FooterActionsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Removed PageArrowIconButton - no longer needed for pagination

const StyledFooterWrapper = styled.div`
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => transparentize(0.9, theme.palette.brand.textPrimary)};
`;

// Removed StyledTablePagination and RotatedArrowIcon - no longer needed

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SaveTemplateButton = styled(Button)`
  background-color: ${({ theme }) => theme.palette.brand.primary};
  text-transform: none;
  font-size: 1.4rem;
  padding: 0 1.5rem;
  color: ${({ theme }) => theme.palette.brand.white};
  font-weight: 500;
  border-radius: 2rem;

  :hover:not(:disabled) {
    background-color: ${({ theme }) => darken(0.1, theme.palette.brand.primary)};
  }

  :disabled {
    background-color: ${({ theme }) => transparentize(0.6, theme.palette.brand.primary)};
    color: ${({ theme }) => transparentize(0.6, theme.palette.brand.white)};
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  background-color: ${({ theme }) => theme.palette.brand.secondary};
  text-transform: none;
  font-size: 1.5rem;
  padding: 0 2rem;
  color: ${({ theme }) => theme.palette.brand.white};
  font-weight: 600;
  border-radius: 2rem;

  :hover:not(:disabled) {
    background-color: ${({ theme }) => darken(0.1, theme.palette.brand.secondary)};
  }

  :disabled {
    background-color: ${({ theme }) => transparentize(0.6, theme.palette.brand.secondary)};
    color: ${({ theme }) => transparentize(0.6, theme.palette.brand.white)};
    cursor: not-allowed;
  }
`;

export { LinkingTableFooter };
